import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import EmailEditor from 'components/EmailEditor';
import { sendEmail, getEmailStatus, deleteEmailStatus } from 'api/email';
import { useAction } from 'hooks/useAction';
import { getSingleLead as getSingleLeadAction } from 'containers/Leads/actions';
import { Loading } from 'components/Loading';
import ThreadsCenter from './components/ThreadsCenter';

export default function LeadsEmailer() {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrations, integrationsEmails: userEmail = [] } = userInfo;
  const leadsById = useSelector(state => state.leads?.leadsById);
  const { orgId, leadId, mode } = useParams();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const returnUrl = searchParams.get('returnUrl');
  const arg = searchParams.get('arg');

  const getSingleLead = useAction(() => getSingleLeadAction({ id: parseInt(leadId, 10) }));
  const currentLead = Object.keys(leadsById).length > 0 && leadsById[leadId] ? leadsById[leadId] : null;

  const [loading, setLoading] = useState(true);

  // If all the integrations for the current user are disconnected, we don't want them to be able to reply.
  const allDisconnected = integrations.every(integration => integration.grantStatus === 'disconnected');
  const readOnlyMode = (userInfo?.permissions['lead.view'] && userEmail.length === 0) || allDisconnected;

  useEffect(() => {
    // Fetch lead data on mount
    getSingleLead();
    // Simulate initial loading - ThreadsCenter will handle its own loading
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <Loading />
    );
  }

  if (mode === 'frame') {
    // Frame mode: Simplified email editor for embedding in iframe
    const [frameEmailState, setFrameEmailState] = useState('');
    const [framePreview, setFramePreview] = useState(false);
    /* eslint-disable-next-line no-unused-vars */
    const [frameActiveThreads, setFrameActiveThreads] = useState([]);
    let frameTimer;

    const formatRecipients = (recipients) => {
      return recipients ? recipients.map((r) => ({ email: r })) : [];
    };

    const formatAttachments = (attachments) => {
      return attachments.map(attachment => {
        return {
          ...attachment,
          content: attachment.content.split(',')[1],
        };
      });
    };

    const fetchFrameEmailStatus = async (id) => {
      try {
        const response = await getEmailStatus(id);
        if (response.ok) {
          const { status } = response.data;
          if (status === 'sent' || status === 'error') {
            clearInterval(frameTimer);
            await deleteEmailStatus(id);
          }
          return status;
        };
        return 'error';
      } catch {
        return 'error';
      }
    };

    const handleFrameEmailSend = async (payload) => {
      try {
        const message = {
          to: formatRecipients(payload.recipients),
          cc: formatRecipients(payload.cc),
          bcc: formatRecipients(payload.bcc),
          reply_to: formatRecipients(payload.replyTo),
          subject: payload.subject,
          body: payload.message,
          attachments: formatAttachments(payload.attachments),
        };

        if (payload.sender) {
          message.from = formatRecipients([payload.sender]);
        }

        const response = await sendEmail({ lead_id: leadId, message });
        if (response.ok && response.data.id) {
          return new Promise((resolve) => {
            frameTimer = setInterval(async () => {
              const status = await fetchFrameEmailStatus(response.data.id);
              if (status === 'sent' || status === 'error') {
                window.parent.postMessage(`email-${status}`);
                resolve(status);
              }
            }, 1000);
          });
        }
        return 'error';
      } catch {
        return 'error';
      }
    };

    const modalSendEmailFrame = async (payload) => {
      setFrameEmailState('sending');
      const result = await handleFrameEmailSend(payload);
      setFrameEmailState(result);
      if (result !== 'error') {
        setFrameEmailState('');
      }
    };

    const handleDiscardFrame = () => {
      if (returnUrl) {
        history.push(returnUrl);
      } else {
        window.parent.postMessage('email-discarded');
      }
    };

    const { contactEmail } = currentLead || {};

    return (
      <EmailEditor
        title='Create a new email.'
        senderEmail={userEmail}
        recipients={contactEmail ? [contactEmail] : []}
        allowCopies
        allowBccSender={false}
        onSubmit={modalSendEmailFrame}
        status={frameEmailState}
        replyMessage={{}}
        onDiscard={handleDiscardFrame}
        threads={frameActiveThreads}
        dataLoaded
        templates={[]}
        mergeFields={[]}
        allowFormatting
        handlePreviewToggle={setFramePreview}
        preview={framePreview}
      />
    );
  }

  return (
    <ThreadsCenter
      orgId={orgId}
      leadId={leadId}
      userInfo={userInfo}
      userEmail={userEmail}
      currentLead={currentLead}
      readOnlyMode={readOnlyMode}
      returnUrl={returnUrl}
      arg={arg}
      mode={mode}
    />
  );
};
